import IconButton from '@material-ui/core/IconButton';
import Errored from '../core/Errored';
import GS from '../grafsync/grafsync';
import SecondaryButton from '../core/SecondaryButton';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import RefreshIcon from '@material-ui/icons/Refresh';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { globalStyles as styles } from '../core/style/gstheme';
import { makeCancelable, sortResources } from '../core/utils/utils';
import { cloneDeep } from 'lodash';
import AccessibleTooltip from '../core/AccessibleTooltip';

const SEGMENT_SIZE = 10;

class QualityCheck extends React.Component {

    state = {
        issues: [],
        sortedResources: [],
        needsLoading: false,
        isErrored: undefined
    };

    static getDerivedStateFromProps(props, state) {
        const { issues } = props;
        if (Array.isArray(issues) && issues.length > 0 && issues.length !== state.issues.length) {
            return {
                needsLoading: true,
                issues: cloneDeep(issues)
            };
        }
        return null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    componentDidUpdate() {
        if (this.state.needsLoading) this.collectData();
    }

    loadData(iris) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph, true));
        return this.cancellable;
    }

    async collectData() {
        try {
            GS.handle("load-quality");
            let iris = [];
            let sortedResources = [];
            if (Array.isArray(this.props.issues) && this.props.issues.length > 0) {
                this.props.issues.forEach(issue => {
                    for (let prop of this.props.loadProps) {
                        if (prop in issue) {
                            const propIRIs = issue[prop];
                            iris = iris.concat(propIRIs);
                        }
                    }
                });
                const dedupedIris = [...new Set(iris)];
                // We split this down to handle the edge case of entailing a huge number of XL labels
                // It also puts a little less load on the server for a huge request
                for (let i = 0; i < dedupedIris.length; i = i + SEGMENT_SIZE) {
                    const segment = dedupedIris.slice(i, i + SEGMENT_SIZE);
                    await this.loadData(segment).promise;
                }
                if (this.cancellable && this.cancellable.isCancelled()) return;
                const unsortedResources = this.props.issues.map(i => i.id);
                sortedResources = sortResources(unsortedResources, this.props.graph, this.props.sortLabel, this.props.defaultLang);
                if (this.cancellable && this.cancellable.isCancelled()) return;
            }
            this.setState({ needsLoading: false, sortedResources });
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e, needsLoading: false });
        }
    }

    render() {

        const { title, issues, issueDisplay, message, dataTest, fromCache, classes, onDisable,
            onRefresh, isLoading, meaning, errors, disabled, isReadOnly } = this.props;

        const { isErrored, sortedResources, needsLoading } = this.state;

        if (isErrored) return <Errored dataTest="qc-error" data={isErrored} />;

        const pending = !disabled && (needsLoading || issues === undefined);
        const delay = new Date(fromCache);
        const passed = errors.length === 0 && Array.isArray(issues) && issues.length === 0;
        const sortedIssues = issues ?
            sortedResources.map(sr => issues.find(i => i.id === sr))
            :
            issues;

        return (
            <div data-test={dataTest}
                style={{
                    backgroundColor: '#f5f5f5',
                    borderBottom: 'solid 1px #c5c5c5',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '12px'
                }}>
                <div style={{ display: 'flex', width: '100%', marginBottom: '12px' }}>
                    <div style={{ marginRight: '24px', flex: 1 }}>
                        <Typography variant="subtitle2" style={{
                            paddingTop: '4px',
                            color: disabled ?
                                '#808080'
                                :
                                pending ?
                                    '#212121'
                                    :
                                    (!passed ?
                                        '#DD2C00'
                                        :
                                        '#50832C'
                                    ),
                            fontSize: '16px'
                        }}>
                            {title}
                        </Typography>
                    </div>
                    {!isLoading && !isReadOnly &&
                        <div>
                            {disabled ?
                                <SecondaryButton compact={true} dataTest="reactivate" onClick={() => {
                                    onDisable(false);
                                    onRefresh();
                                }}>Enable</SecondaryButton>
                                :
                                <AccessibleTooltip title={"Disable check for " + title}>
                                    <IconButton
                                        data-test="disable-check"
                                        aria-label={"Disable check for " + title}
                                        color="inherit"
                                        size="small"
                                        onClick={onDisable}>
                                        <NotInterestedIcon />
                                    </IconButton>
                                </AccessibleTooltip>
                            }
                        </div>
                    }
                </div>
                {!pending && !passed && issues && !disabled &&
                    <div data-test="what-does-it-mean">
                        {meaning ?
                            <div style={{
                                clear: 'both',
                                border: 'solid 1px #c8c8c8',
                                padding: 12,
                                backgroundColor: 'white',
                                borderRadius: 4,
                                marginBottom: 24
                            }}>
                                <Typography paragraph variant="body2" color="textPrimary" style={{ fontWeight: 500 }}>
                                    <span>What Does This Mean?</span>
                                </Typography>
                                {meaning}
                            </div>
                            :
                            null
                        }
                    </div>
                }
                {!passed && !pending && !disabled && delay > 0 &&
                    <div data-test="refresh-info" style={{ width: '100%', display: 'flex' }}>
                        <Typography className={classes.subHeading} style={{ marginBottom: 12, flex: 1 }}>
                            <span data-test="last-run" style={{ display: 'inline-block', marginRight: 12 }} color="textPrimary">Last run at {delay.toLocaleString().substring(12)}</span>
                        </Typography>
                        {!isLoading && <SecondaryButton compact={true} dataTest="refresh" onClick={onRefresh} endIcon={<RefreshIcon />}>Refresh</SecondaryButton>}
                    </div>
                }
                {!pending && message && !passed && !disabled && <Typography variant="body2" paragraph color="textPrimary">{message}</Typography>}
                {!pending && !passed && issues && !disabled &&
                    <div>
                        {sortedIssues.map((issue, index) =>
                            <div data-test={"issue-" + index} key={"issue-" + index}>
                                {issueDisplay(issue, issues)}
                            </div>
                        )}
                    </div>
                }
                {pending && !passed && <Typography data-test="pending" role="status" variant="body2" color="textPrimary">Pending</Typography>}
                {passed && !disabled && <Typography role="status" data-test="no-issues" variant="body2" color="textPrimary">
                    <span>No issues.</span>
                </Typography>}
                {disabled &&
                    <Typography role="status" data-test="check-disabled" variant="body2" color="textPrimary">
                        <span>Disabled</span>
                    </Typography>
                }
                {errors && errors.length > 0 && !disabled && <Typography role="status" data-test="error-non-completion" variant="body2" style={{ color: '#CA4F0C' }}>This check did not complete. This is most likely due to it taking too long to run.</Typography>}
            </div>
        );
    }
}

QualityCheck.defaultProps = {
    sortLabel: "label",
    errors: []
};

QualityCheck.propTypes = {
    dataTest: PropTypes.string,
    message: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    meaning: PropTypes.object,
    sortLabel: PropTypes.string,
    fromCache: PropTypes.number,
    title: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    errors: PropTypes.array,
    issues: PropTypes.array,
    isReadOnly: PropTypes.bool,
    isLoading: PropTypes.bool.isRequired,
    onRefresh: PropTypes.func.isRequired,
    issueDisplay: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(QualityCheck);